import { useState, type FormEvent } from "react";
import {
  confirmForgotPassword,
  forgotPassword,
  signIn,
  type AuthError,
  type SignInResult,
} from "./cognito.js";

interface AdminLoginProps {
  /** Called with the Cognito ID token once the owner is fully signed in. */
  onSignedIn: (idToken: string) => void;
}

/** The login surface can be in one of these steps. */
type Mode = "signIn" | "newPassword" | "forgotRequest" | "forgotConfirm";

/**
 * Friendly owner sign-in (Req 3.1, 3.2).
 *
 * A normal email + password form backed by Cognito SRP — no tokens to paste.
 * It also handles two first-class flows:
 *   - the first-login forced password change (Cognito `NEW_PASSWORD_REQUIRED`),
 *     shown as a simple "set a new password" step, and
 *   - "Forgot password?", which emails a code and lets the owner set a new
 *     password.
 *
 * Inline errors explain wrong credentials, every action shows a loading state,
 * fields carry labels + autocomplete hints, and each step submits on Enter.
 */
export function AdminLogin({ onSignedIn }: AdminLoginProps): JSX.Element {
  const [mode, setMode] = useState<Mode>("signIn");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Holds the challenge continuation when Cognito requires a new password.
  const [completeNewPassword, setCompleteNewPassword] = useState<
    ((newPassword: string) => Promise<SignInResult>) | null
  >(null);

  const showError = (err: AuthError): void => setError(err.message);

  /** Routes a sign-in outcome to success / new-password / error handling. */
  const handleResult = (result: SignInResult): void => {
    if (result.kind === "success") {
      onSignedIn(result.idToken);
      return;
    }
    if (result.kind === "newPasswordRequired") {
      setCompleteNewPassword(() => result.complete);
      setNewPassword("");
      setError(null);
      setNotice("Welcome! Please choose a new password to finish setting up your account.");
      setMode("newPassword");
      return;
    }
    showError(result.error);
  };

  const handleSignIn = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setError(null);
    setLoading(true);
    try {
      handleResult(await signIn(email.trim(), password));
    } finally {
      setLoading(false);
    }
  };

  const handleNewPassword = async (
    event: FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    event.preventDefault();
    if (completeNewPassword === null) {
      return;
    }
    setError(null);
    setLoading(true);
    try {
      handleResult(await completeNewPassword(newPassword));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotRequest = async (
    event: FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    event.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const result = await forgotPassword(email.trim());
      if (result.ok) {
        setNotice(`We've emailed a verification code to ${email.trim()}.`);
        setNewPassword("");
        setCode("");
        setMode("forgotConfirm");
      } else {
        showError(result.error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotConfirm = async (
    event: FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    event.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const result = await confirmForgotPassword(
        email.trim(),
        code.trim(),
        newPassword,
      );
      if (result.ok) {
        setPassword("");
        setNotice("Your password has been updated. You can sign in now.");
        setMode("signIn");
      } else {
        showError(result.error);
      }
    } finally {
      setLoading(false);
    }
  };

  const goTo = (next: Mode): void => {
    setError(null);
    setNotice(null);
    setMode(next);
  };

  return (
    <div className="admin-login">
      {mode === "signIn" ? (
        <form className="admin-login__card" onSubmit={handleSignIn}>
          <h1 className="admin-login__title">Owner sign in</h1>
          <p className="admin-login__hint">
            Sign in with your email and password to manage the site.
          </p>
          {renderNotice(notice)}
          {renderError(error)}
          <div className="admin-field">
            <label className="admin-field__label" htmlFor="admin-email">
              Email
            </label>
            <input
              id="admin-email"
              className="admin-field__input"
              type="email"
              autoComplete="username"
              autoFocus
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </div>
          <PasswordField
            id="admin-password"
            label="Password"
            autoComplete="current-password"
            value={password}
            onChange={setPassword}
          />
          <button
            type="submit"
            className="admin-button admin-button--primary"
            disabled={loading || email.trim().length === 0 || password.length === 0}
          >
            {loading ? "Signing in\u2026" : "Sign in"}
          </button>
          <button
            type="button"
            className="admin-login__link"
            onClick={() => goTo("forgotRequest")}
          >
            Forgot password?
          </button>
        </form>
      ) : null}

      {mode === "newPassword" ? (
        <form className="admin-login__card" onSubmit={handleNewPassword}>
          <h1 className="admin-login__title">Set a new password</h1>
          {renderNotice(notice)}
          {renderError(error)}
          <PasswordField
            id="admin-new-password"
            label="New password"
            autoComplete="new-password"
            autoFocus
            showRules
            value={newPassword}
            onChange={setNewPassword}
          />
          <button
            type="submit"
            className="admin-button admin-button--primary"
            disabled={loading || !passwordValid(newPassword)}
          >
            {loading ? "Saving\u2026" : "Set password and continue"}
          </button>
        </form>
      ) : null}

      {mode === "forgotRequest" ? (
        <form className="admin-login__card" onSubmit={handleForgotRequest}>
          <h1 className="admin-login__title">Reset your password</h1>
          <p className="admin-login__hint">
            Enter your email and we'll send you a verification code.
          </p>
          {renderNotice(notice)}
          {renderError(error)}
          <div className="admin-field">
            <label className="admin-field__label" htmlFor="admin-forgot-email">
              Email
            </label>
            <input
              id="admin-forgot-email"
              className="admin-field__input"
              type="email"
              autoComplete="username"
              autoFocus
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </div>
          <button
            type="submit"
            className="admin-button admin-button--primary"
            disabled={loading || email.trim().length === 0}
          >
            {loading ? "Sending\u2026" : "Send code"}
          </button>
          <button
            type="button"
            className="admin-login__link"
            onClick={() => goTo("signIn")}
          >
            Back to sign in
          </button>
        </form>
      ) : null}

      {mode === "forgotConfirm" ? (
        <form className="admin-login__card" onSubmit={handleForgotConfirm}>
          <h1 className="admin-login__title">Enter your code</h1>
          {renderNotice(notice)}
          {renderError(error)}
          <div className="admin-field">
            <label className="admin-field__label" htmlFor="admin-code">
              Verification code
            </label>
            <input
              id="admin-code"
              className="admin-field__input"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              required
              value={code}
              onChange={(event) => setCode(event.target.value)}
            />
          </div>
          <PasswordField
            id="admin-reset-password"
            label="New password"
            autoComplete="new-password"
            showRules
            value={newPassword}
            onChange={setNewPassword}
          />
          <button
            type="submit"
            className="admin-button admin-button--primary"
            disabled={
              loading || code.trim().length === 0 || !passwordValid(newPassword)
            }
          >
            {loading ? "Updating\u2026" : "Update password"}
          </button>
          <button
            type="button"
            className="admin-login__link"
            onClick={() => goTo("signIn")}
          >
            Back to sign in
          </button>
        </form>
      ) : null}
    </div>
  );
}

/** The individual password-strength rules (mirrors the Cognito pool policy). */
function passwordChecks(pwd: string): {
  length: boolean;
  upper: boolean;
  lower: boolean;
  number: boolean;
  symbol: boolean;
} {
  return {
    length: pwd.length >= 12,
    upper: /[A-Z]/.test(pwd),
    lower: /[a-z]/.test(pwd),
    number: /[0-9]/.test(pwd),
    symbol: /[^A-Za-z0-9]/.test(pwd),
  };
}

/** True when a password satisfies every rule the Cognito pool requires. */
function passwordValid(pwd: string): boolean {
  const c = passwordChecks(pwd);
  return c.length && c.upper && c.lower && c.number && c.symbol;
}

interface PasswordFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  autoComplete: string;
  autoFocus?: boolean;
  /** When true, shows the live requirements checklist below the field. */
  showRules?: boolean;
}

/**
 * A password input with a show/hide toggle, and (when `showRules`) a live
 * checklist of the required practices so the owner sees the rules up front
 * instead of hitting a server error after submitting.
 */
function PasswordField({
  id,
  label,
  value,
  onChange,
  autoComplete,
  autoFocus,
  showRules,
}: PasswordFieldProps): JSX.Element {
  const [visible, setVisible] = useState(false);
  const checks = showRules ? passwordChecks(value) : null;
  return (
    <div className="admin-field">
      <label className="admin-field__label" htmlFor={id}>
        {label}
      </label>
      <div className="admin-field__password">
        <input
          id={id}
          className="admin-field__input admin-field__input--password"
          type={visible ? "text" : "password"}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          required
          value={value}
          onChange={(event) => onChange(event.target.value)}
        />
        <button
          type="button"
          className="admin-field__toggle"
          aria-pressed={visible}
          aria-label={visible ? "Hide password" : "Show password"}
          onClick={() => setVisible((previous) => !previous)}
        >
          {visible ? "Hide" : "Show"}
        </button>
      </div>
      {checks ? (
        <ul className="admin-password-rules" aria-label="Password requirements">
          {(
            [
              ["At least 12 characters", checks.length],
              ["An uppercase letter", checks.upper],
              ["A lowercase letter", checks.lower],
              ["A number", checks.number],
              ["A symbol (! @ # $ …)", checks.symbol],
            ] as [string, boolean][]
          ).map(([text, ok]) => (
            <li
              key={text}
              className={
                ok
                  ? "admin-password-rules__item admin-password-rules__item--ok"
                  : "admin-password-rules__item"
              }
            >
              <span aria-hidden="true">{ok ? "\u2713" : "\u25CB"}</span> {text}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/** Renders an inline error message (assertive for screen readers). */
function renderError(message: string | null): JSX.Element | null {
  if (message === null) {
    return null;
  }
  return (
    <p className="admin-login__error" role="alert">
      {message}
    </p>
  );
}

/** Renders an informational notice (polite for screen readers). */
function renderNotice(message: string | null): JSX.Element | null {
  if (message === null) {
    return null;
  }
  return (
    <p className="admin-login__notice" role="status">
      {message}
    </p>
  );
}
